import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  contentSelectors,
  contentOperations,
} from '../models/content/index.js';
import type { Content } from '../models/content/types';
import ContentService from '../services/content';
import { usePatronSelector } from './use-patron-selector';
import { AxiosResponse } from 'axios';

type MentionFormat = 'raw' | 'with_metadata';

interface UseContentFetcherProps {
  contentId: number;
  feedItemId?: string;
  isApproval?: boolean;
  isUgcPreview?: boolean;
  mentionFormat?: MentionFormat;
}

function useContentFetcher({
  contentId,
  feedItemId,
  isApproval,
  isUgcPreview,
  mentionFormat,
}: UseContentFetcherProps) {
  const dispatch = useDispatch();

  const [fetching, setFetching] = useState(true);
  const [content, setContent] = useState<Content>();
  const [error, setError] = useState<unknown>();

  const contentItem = usePatronSelector((state) =>
    contentSelectors.getContentById(state, { contentId })
  );

  const addContentToStore = (c: Content) =>
    dispatch(contentOperations.addContent(c));

  useEffect(() => {
    // FE-34414: always re-fetch for the UGC author preview so the rendered post
    // view reflects the latest publication_state rather than the cached draft.
    if (contentItem && !isUgcPreview) {
      setContent(contentItem);
      setFetching(false);
    } else {
      fetchContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, feedItemId, isApproval, isUgcPreview]);

  const fetchContent = () => {
    setFetching(true);

    const createFetchPromise = (): Promise<AxiosResponse> => {
      if (feedItemId) {
        return new ContentService().fetchByFeedItemId(feedItemId);
      }
      if (isApproval) {
        return new ContentService(contentId).fetchApproval();
      }
      if (isUgcPreview) {
        return new ContentService(contentId).fetchUgcPreview();
      }
      return new ContentService(contentId).fetch({
        mention_format: mentionFormat,
      });
    };

    createFetchPromise()
      .then(handleFetchContentSuccess)
      .catch(handleFetchContentError);
  };

  const handleFetchContentSuccess = (res: AxiosResponse) => {
    const contentItem = res.data.feed
      ? res.data.feed[0].attributes
      : res.data.attributes;

    if (res.data.meta?.approval_state) {
      contentItem.approval_state = res.data.meta.approval_state;
    }

    addContentToStore(contentItem);

    setTimeout(() => {
      setContent(contentItem);
      setFetching(false);
    }, 500);
  };

  const handleFetchContentError = (err: unknown) => {
    console.error(err);

    setError(err);
    setFetching(false);
  };

  return {
    fetching,
    content,
    error,
  };
}

export default useContentFetcher;
